import { Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { PathToLiveConfiguration } from 'src/app/models/blueprints/path-to-live-configuration';
import { PathToLiveTemplate } from 'src/app/models/blueprints/path-to-live-template';
import { DataZone } from 'src/app/models/data-zone/data-zone';
import { DeploymentClass } from 'src/app/models/instance-class';
import { Solution } from 'src/app/models/service/solution';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { PathToLiveEnvironment } from 'src/app/models/blueprints/path-to-live-environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Constants } from 'src/app/models/constants';
import { GenericErrorComponent } from 'src/app/components/generic-error/generic-error.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-path-to-live-workflow',
    templateUrl: './path-to-live-workflow.component.html',
    styleUrls: ['./path-to-live-workflow.component.scss'],
    standalone: false
})
export class PathToLiveWorkflowComponent implements OnInit{

  @Input() service: Solution;
  @Input() pathToLiveConfiguration: PathToLiveConfiguration[];
  @Input() devInstance;
  @Input() tenant;
private destroyRef = inject(DestroyRef);
  DataZone = DataZone;

  pathToLiveTemplates: PathToLiveTemplate[];
  selectedPathToLiveTemplate: PathToLiveTemplate;

  pathToLiveTestForm: FormGroup;
  pathToLivePiiForm: FormGroup;

  edit = false;
  Constants = Constants;
  isWorkflowEnabled: boolean;

  constructor(private formBuilder: FormBuilder, private blueprintsService: BlueprintsService, public sidenavService: SidenavService,
    private matDialog: MatDialog) {
  }

  get testFormArray(): FormArray {
    return this.pathToLiveTestForm.controls.pathToLiveConfiguration as FormArray;
  }

  get piiFormArray(): FormArray {
    return this.pathToLivePiiForm.controls.pathToLiveConfiguration as FormArray;
  }

  ngOnInit(): void {
    this.isWorkflowEnabled = true;
    this.createPathToLiveForm();

    if (Array.isArray(this.pathToLiveConfiguration) && this.pathToLiveConfiguration.length) {
      this.edit = true;
    } else {
      this.setDefaultPathToLiveTemplate();
      this.pathToLiveTemplateChanged();
    }
  }

  pathToLiveTemplateChanged(): void {
    this.testFormArray.clear();
    this.piiFormArray.clear();

    this.pathToLiveConfigurationToFormArrays(this.selectedPathToLiveTemplate.configuration);
  }

  addToTestDataZone(): void {
    this.testFormArray.insert(this.testFormArray.length, this.pathToLiveConfigurationToFormGroup({
      editable: true,
      environment: '',
      dataZone: DataZone.DEVELOPMENT,
      deploymentClass: DeploymentClass.C,
    }));
    if(this.pathToLiveConfiguration.length === this.testFormArray.length + this.piiFormArray.length) {
      this.isWorkflowEnabled = true;
    } else {
      this.isWorkflowEnabled = false;
    }
  }

  addToPiiDataZone(): void {
    this.piiFormArray.insert(0, this.pathToLiveConfigurationToFormGroup({
      editable: true,
      environment: '',
      dataZone: DataZone.SECURE,
      deploymentClass: DeploymentClass.B,
    }));
    if(this.pathToLiveConfiguration.length === this.testFormArray.length + this.piiFormArray.length) {
      this.isWorkflowEnabled = true;
    } else {
      this.isWorkflowEnabled = false;
    }
  }

  deleteEnvironment(form: FormGroup, index: number): void {
    (form.controls.pathToLiveConfiguration as FormArray).removeAt(index);
    this.updateNotUniqueValidator();
    if(this.pathToLiveConfiguration.length === this.testFormArray.length + this.piiFormArray.length) {
      this.isWorkflowEnabled = true;
    } else {
      this.isWorkflowEnabled = false;
    }
  }

  saveWorkflow(): void {
    const pathToLiveTestFormValid = FormUtilService.validateForm(this.pathToLiveTestForm);
    const pathToLivePiiFormValid = FormUtilService.validateForm(this.pathToLivePiiForm);

    if (pathToLiveTestFormValid && pathToLivePiiFormValid) {
      const piiForm = this.pathToLivePiiForm.getRawValue().pathToLiveConfiguration;
      if (piiForm[piiForm.length-2].environment !== PathToLiveEnvironment.PREPROD ||
        piiForm[piiForm.length-1].environment !== PathToLiveEnvironment.LIVE) {
        this.matDialog.open(GenericErrorComponent, {
          minHeight: '342px',
          width: '566px',
          panelClass: 'flex-dialog'
        });
      } else {
        const pathToLiveConfigurations = [...this.pathToLiveTestForm.getRawValue().pathToLiveConfiguration,
          ...this.pathToLivePiiForm.getRawValue().pathToLiveConfiguration];
        this.updateRequiresFields(pathToLiveConfigurations);

        pathToLiveConfigurations.forEach(pathToLiveConfiguration => {
          if (pathToLiveConfiguration.instanceId == null) {
            // Workaround for a bug in fabric which sends/receives instanceId as empty object {} if it is null
            delete pathToLiveConfiguration.instanceId;
          }
        });

        this.sidenavService.closeRightSidenav(pathToLiveConfigurations);
      }
    }
  }

  private createPathToLiveForm(): void {
    this.pathToLiveTestForm = this.formBuilder.group({
      pathToLiveConfiguration: this.formBuilder.array([])
    });
    this.pathToLivePiiForm = this.formBuilder.group({
      pathToLiveConfiguration: this.formBuilder.array([])
    });

    if (this.pathToLiveConfiguration) {
      this.pathToLiveConfigurationToFormArrays(this.pathToLiveConfiguration);
    }
  }

  private pathToLiveConfigurationToFormArrays(pathToLiveConfiguration: PathToLiveConfiguration[]): void {
    pathToLiveConfiguration.forEach(pathToLiveConfig => {
      if (pathToLiveConfig.dataZone === DataZone.DEVELOPMENT) {
        this.testFormArray.push(this.pathToLiveConfigurationToFormGroup(pathToLiveConfig));
      } else {
        this.piiFormArray.push(this.pathToLiveConfigurationToFormGroup(pathToLiveConfig));
      }
    });
  }

  private loadPathToLiveTemplates(): void {
    this.blueprintsService.getPathToLiveTemplates()
        .pipe(tap(pathToLiveTemplates => {
          if (pathToLiveTemplates.length) {
            this.selectedPathToLiveTemplate = pathToLiveTemplates[0];
          }

          this.pathToLiveTemplateChanged();
        }), takeUntilDestroyed(this.destroyRef))
        .subscribe(result => this.pathToLiveTemplates = result);
  }

  private pathToLiveConfigurationToFormGroup(pathToLiveConfiguration: PathToLiveConfiguration): FormGroup {
    return this.formBuilder.group({
      environment: [{value: pathToLiveConfiguration.environment, disabled: !pathToLiveConfiguration.editable},
        [Validators.required, this.uniqueValidator(), Validators.maxLength(16), Validators.pattern(/^[a-zA-Z]+$/)]],
      requires: [pathToLiveConfiguration.requires],
      editable: [pathToLiveConfiguration.editable],
      dataZone: [pathToLiveConfiguration.dataZone],
      deploymentClass: [pathToLiveConfiguration.deploymentClass],
      instanceId: [pathToLiveConfiguration.instanceId],
    });
  }

  private updateRequiresFields(pathToLiveConfigurations: PathToLiveConfiguration[]): void {
    if (pathToLiveConfigurations.length > 0) {
      pathToLiveConfigurations[0].requires = undefined;
      for (let index = 1; index < pathToLiveConfigurations.length; index++) {
        pathToLiveConfigurations[index].requires = pathToLiveConfigurations[index - 1].environment;
      }
    }
  }

  private uniqueValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const allControls = [...this.testFormArray.controls, ...this.piiFormArray.controls];

      setTimeout(() => {
        this.updateNotUniqueValidator();
      });

      const haveSameEnvironment = allControls.some(aControl => aControl.get('environment') !== control
        && aControl.get('environment')?.value.toLowerCase() === control.value.toLowerCase());

      let hasDevEnvironment;
      if(this.devInstance?.length) {
          this.devInstance.forEach((item) => {
           const find =  allControls.some(controls => controls?.value?.environment?.toLowerCase() === item?.name?.toLowerCase());
           if (find) {
            hasDevEnvironment = true;
            return;
           }
          });
      }

      return haveSameEnvironment || hasDevEnvironment? { notUniqueEnvironment: true } : null;
    };
  }

  private updateNotUniqueValidator(): void {
    const allControls = [...this.testFormArray.controls, ...this.piiFormArray.controls];
    let hasDevInstance;
    allControls.forEach(aControl => {
      const hasDuplicate = allControls.some(bControl => aControl !== bControl
        && aControl.get('environment')?.value.toLowerCase() === bControl.get('environment').value.toLowerCase());

      if (!hasDuplicate) {
        if(this.devInstance?.length) {
          hasDevInstance = this.devInstance.find(item => item?.name?.toLowerCase() === aControl?.value?.environment?.toLowerCase());
        }
      }
      if (!hasDuplicate && !hasDevInstance && aControl.get('environment').hasError('notUniqueEnvironment')) {
        aControl.get('environment').setErrors({ notUniqueEnvironment: undefined });
        aControl.get('environment').updateValueAndValidity();
      }
    });


  }

  private setDefaultPathToLiveTemplate(): void {
    this.selectedPathToLiveTemplate = {
      displayName: 'Default',
      configuration: [{
          editable: false,
          environment: PathToLiveEnvironment.PREPROD,
          dataZone: DataZone.SECURE,
          deploymentClass: DeploymentClass.B
        }, {
          editable: false,
          environment: PathToLiveEnvironment.LIVE,
          requires: PathToLiveEnvironment.PREPROD,
          dataZone: DataZone.LIVE,
          deploymentClass: DeploymentClass.A
          //deploymentClass: this.tenant?.parameters?.prodDeploymentClass ? this.tenant?.parameters?.prodDeploymentClass : DeploymentClass.A
      }]
    };
  }
}
